import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import RequiredMark from './RequiredMark';
import type { IconName } from './Icon';

export type AutocompleteOption = { id: number; label: string };

export type AutocompleteCreateOption = {
  label: string;
  icon?: IconName;
  /** Use onClick for in-page actions (e.g. open a modal). */
  onClick?: () => void;
  /** Use href + target for navigation (e.g. open a new tab). */
  href?: string;
  target?: string;
};

interface AutocompleteInputProps {
  label?: string;
  /** Required field: a red asterisk after the label and `aria-required`
   *  (Standards §14.7). */
  markRequired?: boolean;
  placeholder?: string;
  options: AutocompleteOption[];
  value: AutocompleteOption | null;
  onChange: (opt: AutocompleteOption | null) => void;
  className?: string;
  /** Material Symbol icon shown on the left in primary colour. */
  icon?: IconName;
  /** Called on every keystroke — use to trigger external async search with 300 ms debounce. */
  onQueryChange?: (q: string) => void;
  /** Show spinner and "Buscando..." while loading results. */
  loading?: boolean;
  /**
   * Always shown at the bottom of the dropdown (even when there are results).
   * Use for "create new" actions. Rendered with the standard create-option style:
   * primary colour, bold, icon on the left, subtle background.
   */
  createOption?: AutocompleteCreateOption;
  /** Validation error message shown below the input (red border + message). */
  error?: string;
  /** aria-label for the clear (X) button. Default Spanish
   *  ("Limpiar selección"). */
  clearLabel?: string;
  /** Text shown in the dropdown while async results load. Default Spanish
   *  ("Buscando..."). */
  loadingLabel?: string;
}

/**
 * Search-select input with floating label, optional leading icon, and async support.
 *
 * Standard component for ALL "select an existing record" fields across admin and tenant.
 * Follows the same floating-label pattern as Input / Select.
 *
 * When the user should also be able to create a new record, pass `createOption`.
 * It is always shown at the bottom of the dropdown, matching the client-selector
 * style used in EditOrderModal.
 */
export function AutocompleteInput({
  label,
  markRequired = false,
  placeholder = 'Buscar...',
  options,
  value,
  onChange,
  className = '',
  icon,
  onQueryChange,
  loading = false,
  createOption,
  error,
  clearLabel = 'Limpiar selección',
  loadingLabel = 'Buscando...',
}: AutocompleteInputProps) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setText(value ? value.label : '');
  }, [value]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        if (!value || text !== value.label) setText(value ? value.label : '');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [text, value]);

  const filtered = text.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(text.toLowerCase()))
    : options;

  function select(opt: AutocompleteOption) {
    onChange(opt);
    setText(opt.label);
    setOpen(false);
  }

  function clear() {
    onChange(null);
    setText('');
    setOpen(false);
    onQueryChange?.('');
  }

  const hasError = Boolean(error);
  const borderClass = hasError
    ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500'
    : 'border-border-strong dark:border-border-strong-dark focus:ring-primary focus:border-primary';

  const inputClass = [
    'h-10 w-full rounded-md border bg-transparent',
    borderClass,
    icon ? 'pl-10' : 'px-3',
    'pr-7 text-sm text-text-main dark:text-white placeholder-slate-400 dark:placeholder-slate-600',
    'shadow-sm transition-all focus:outline-none focus:ring-1',
  ].join(' ');

  const showDropdown = open && (loading || filtered.length > 0 || Boolean(createOption));

  const createOptionClass =
    'flex w-full cursor-pointer items-center gap-2 bg-slate-50/50 px-4 py-2.5 text-sm font-bold text-primary hover:bg-slate-100 dark:bg-slate-800/50 dark:hover:bg-slate-700/50';

  return (
    <div ref={wrapRef} className={`relative ${label ? 'pt-2' : ''} ${className}`}>
      {label && (
        <span className="absolute top-0 left-2 px-1 bg-white dark:bg-surface-dark text-[10px] font-bold text-primary uppercase tracking-wider z-10 pointer-events-none truncate max-w-[calc(100%-1rem)]">
          {label}
          {markRequired && <> <RequiredMark /></>}
        </span>
      )}

      <div className="relative flex items-center">
        {icon && (
          <div className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-0 pl-3 flex items-center text-primary">
            <Icon name={icon} className="!text-[18px]" />
          </div>
        )}

        <input
          type="text"
          value={text}
          placeholder={placeholder}
          aria-required={markRequired || undefined}
          className={inputClass}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            if (!e.target.value) onChange(null);
            onQueryChange?.(e.target.value);
          }}
          onFocus={() => setOpen(true)}
        />

        {value && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            aria-label={clearLabel}
          >
            <Icon name="close" className="!text-[16px]" />
          </button>
        )}
      </div>

      {showDropdown && (
        <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-border-subtle bg-white shadow-2xl dark:border-border-subtle-dark dark:bg-surface-dark">
          {loading ? (
            <li className="flex items-center gap-2 px-4 py-3 text-sm text-slate-400">
              <Icon name="progress_activity" className="!text-[16px] animate-spin" />
              {loadingLabel}
            </li>
          ) : (
            <>
              {filtered.map((opt) => (
                <li
                  key={opt.id}
                  onMouseDown={() => select(opt)}
                  className="cursor-pointer border-b border-border-subtle px-4 py-2.5 last:border-0 hover:bg-slate-50 dark:border-slate-700/50 dark:hover:bg-slate-700"
                >
                  <div className="text-sm font-bold text-primary">{opt.label}</div>
                </li>
              ))}
              {createOption && (
                <li>
                  {createOption.href ? (
                    <a
                      href={createOption.href}
                      target={createOption.target}
                      rel={createOption.target === '_blank' ? 'noopener noreferrer' : undefined}
                      className={createOptionClass}
                    >
                      {createOption.icon && (
                        <Icon name={createOption.icon} className="!text-[16px]" />
                      )}
                      {createOption.label}
                    </a>
                  ) : (
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); createOption.onClick?.(); setOpen(false); }}
                      className={createOptionClass}
                    >
                      {createOption.icon && (
                        <Icon name={createOption.icon} className="!text-[16px]" />
                      )}
                      {createOption.label}
                    </button>
                  )}
                </li>
              )}
            </>
          )}
        </ul>
      )}

      {error && <p className="mt-1 text-xs font-medium text-red-500">{error}</p>}
    </div>
  );
}
